import { jsPDF } from 'jspdf';
import hedenaLogo from '../assets/HEDENALOGO.jpeg';

export const getImgFormat = (src) => {
    if (!src) return 'PNG';
    if (src.startsWith('data:image/jpeg') || src.endsWith('.jpg') || src.endsWith('.jpeg')) return 'JPEG';
    if (src.startsWith('data:image/webp') || src.endsWith('.webp')) return 'WEBP';
    return 'PNG';
};

export const replacePlaceholders = (text, data, targetRole) => {
    if (!text) return '';
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

    let replaced = text;

    // Fix for WYSIWYG editor injecting newlines/paragraphs before the date
    // We target anything between "is executed on this" and "{dateStr}" that is a block tag (like p, div, br)
    replaced = replaced.replace(/is executed on this\s*<\/?(p|div|br)[^>]*>\s*(<[^>]+>)*\s*{dateStr}/gi, (match, p1) => {
        // preserve the inner tag (like <strong>) if it exists right before {dateStr}
        const innerTag = p1 || '';
        return `is executed on this ${innerTag}{dateStr}`;
    });
    // Fallback if there are multiple tags
    replaced = replaced.replace(/is executed on this[\s\S]{0,50}?{dateStr}/gi, (match) => {
        // extract only inline styling tags like <b>, <strong>, <span>, <i>
        const inlineTags = match.match(/<(strong|b|span|i|em|u)[^>]*>/gi) || [];
        return `is executed on this ${inlineTags.join('')}{dateStr}`;
    });

    replaced = replaced.replace(/{dateStr}/g, dateStr);

    replaced = replaced.replace(
        /is executed on this\s*<\/p>\s*<p>\s*/gi,
        'is executed on this '
    );


    const formattedAddress = data.address
        ? (typeof data.address === 'string' ? data.address : `${data.address.street || ''}, ${data.address.city || ''}, ${data.address.state || ''} - ${data.address.pincode || ''}`.trim().replace(/^,|,$/g, '').trim())
        : '________________________________________________';

    if (targetRole === 'doctor') {
        const docName = data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || '____________________';
        const regNum = data.registrationNumber || '_________________';
        replaced = replaced.replace(/{docName}/g, docName);
        replaced = replaced.replace(/{regNum}/g, regNum);
        replaced = replaced.replace(/{address}/g, formattedAddress);
    } else if (targetRole === 'hospital') {
        const rawHospitalName = data.hospitalName || data.onboardingData?.hospitalName || data.firstName || '____________________';
        replaced = replaced.replace(/{hospitalName}\s*Hospital\s*\/\s*Nursing\s*Home\s*\/\s*Healthcare\s*Facility/gi, rawHospitalName);
        replaced = replaced.replace(/{hospitalName}/g, rawHospitalName);
        replaced = replaced.replace(/{address}/g, formattedAddress);
    } else if (targetRole.includes('marketing') || targetRole === 'marketing_agent') {
        const partnerName = data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || '____________________';
        replaced = replaced.replace(/{partnerName}/g, partnerName);
        replaced = replaced.replace(/{address}/g, formattedAddress);
    } else if (targetRole === 'patient') {
        const patientName = data.fullName || data.patientName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || '____________________';
        const patientPhone = data.phone || data.patientPhone || '_________________';
        const patientEmail = data.email || data.patientEmail || '_________________';
        replaced = replaced.replace(/{patientName}/g, patientName);
        replaced = replaced.replace(/{patientPhone}/g, patientPhone);
        replaced = replaced.replace(/{patientEmail}/g, patientEmail);
        replaced = replaced.replace(/{address}/g, formattedAddress);
    }

    return replaced;
};

const addDynamicContentToPDF = (doc, htmlContent, addTitle, addHeading, addParagraph, addTable, addRichParagraph) => {
    if (!htmlContent) return;

    // Merge adjacent tables (ignoring any number of whitespace/empty tags/styles between them) before splitting.
    // (?:<[^>]+>|\s|&nbsp;)* safely matches any tags and spaces but WILL STOP if there is actual text!
    let processedHtml = htmlContent.replace(/<\/table>(?:<[^>]+>|\s|&nbsp;)*<table[^>]*>/gi, '');

    // Use regex-based string splitting to cleanly separate Tables from Text
    const segments = processedHtml.split(/(<table[\s\S]*?<\/table>)/gi);

    const parseRichText = (htmlString) => {
        const temp = document.createElement('div');
        temp.innerHTML = htmlString;
        const tokens = [];

        function walk(node, currentStyle) {
            if (node.nodeType === 3) {
                const text = node.textContent.replace(/\n/g, ' ');
                if (text) tokens.push({ text, style: { ...currentStyle } });
            } else if (node.nodeType === 1) {
                if (node.tagName === 'LI') {
                    tokens.push({ text: '\n• ', style: { ...currentStyle } });
                } else if (['BR', 'P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(node.tagName)) {
                    tokens.push({ text: '\n', style: {} });
                }

                let style = { ...currentStyle };
                if (['B', 'STRONG'].includes(node.tagName)) style.bold = true;
                if (['I', 'EM'].includes(node.tagName)) style.italic = true;
                if (node.tagName === 'U') style.underline = true;

                if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(node.tagName)) {
                    style.isHeadingTag = true;
                    style.bold = true;
                }

                if (node.style && node.style.color) style.color = node.style.color;

                Array.from(node.childNodes).forEach(child => walk(child, style));

                if (['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(node.tagName)) {
                    tokens.push({ text: '\n', style: {} });
                }
            }
        }
        walk(temp, {});
        return tokens;
    };

    segments.forEach(seg => {
        if (!seg.trim()) return;

        if (/^<table/i.test(seg.trim())) {
            // Parse TABLE into 2D array
            const tableData = [];
            const rowMatches = [...seg.matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)];
            rowMatches.forEach(rowM => {
                const cells = [];
                const cellMatches = [...rowM[1].matchAll(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi)];
                cellMatches.forEach(cellM => {
                    const cellText = cellM[1]
                        .replace(/<[^>]+>/g, '')
                        .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/&quot;/g, '"')
                        .replace(/₹/g, 'Rs. ')
                        .replace(/\s+/g, ' ').trim();
                    cells.push(cellText);
                });
                if (cells.length > 0) tableData.push(cells);
            });
            if (tableData.length > 0) addTable(tableData);
        } else {
            const tokens = parseRichText(seg);
            let paragraphs = [];
            let currentPara = [];

            tokens.forEach(t => {
                if (t.text === '\n') {
                    if (currentPara.length > 0) paragraphs.push(currentPara);
                    currentPara = [];
                } else {
                    currentPara.push(t);
                }
            });
            if (currentPara.length > 0) paragraphs.push(currentPara);

            const newParagraphs = [];
            paragraphs.forEach(paraTokens => {
                const fullText = paraTokens.map(t => t.text).join('');
                const match = fullText.match(/\s+(\d+(\.\d+)*\.?\s+[A-Z])/);

                // Exclude dates from being split (e.g. "4 July 2026")
                let isDatePattern = false;
                if (match) {
                    const matchText = fullText.substring(match.index).trim();
                    isDatePattern = /^\d+\s+(january|february|march|april|may|june|july|august|september|october|november|december)/i.test(matchText);
                }

                if (match && !isDatePattern) {
                    const splitIndex = match.index;
                    let p1 = [];
                    let p2 = [];
                    let currentLen = 0;

                    paraTokens.forEach(t => {
                        if (currentLen >= splitIndex) {
                            p2.push(t);
                        } else if (currentLen + t.text.length > splitIndex) {
                            const splitPoint = splitIndex - currentLen;
                            p1.push({ text: t.text.substring(0, splitPoint), style: t.style });
                            p2.push({ text: t.text.substring(splitPoint).replace(/^\s+/, ''), style: t.style });
                        } else {
                            p1.push(t);
                        }
                        currentLen += t.text.length;
                    });
                    if (p1.length > 0) newParagraphs.push(p1);
                    if (p2.length > 0) newParagraphs.push(p2);
                } else {
                    newParagraphs.push(paraTokens);
                }
            });

            newParagraphs.forEach(paraTokens => {
                let fullText = paraTokens.map(t => t.text).join('').replace(/\s+/g, ' ').trim();
                if (!fullText) return;

                const startsWithNumber = /^\d+(\.\d+)*\.?\s+/.test(fullText);
                const isAllUppercase = /^[A-Z0-9\s\.\&\-\(\)]+$/.test(fullText);
                const isShort = fullText.length < 100;

                const hasHeadingTag = paraTokens.some(t => t.style?.isHeadingTag);

                let isHeading = hasHeadingTag;
                if (!isHeading) {
                    if (isAllUppercase && isShort) {
                        if (fullText.length >= 15 || startsWithNumber) {
                            isHeading = true;
                        }
                    } else if (startsWithNumber && isShort) {
                        const isDate = /january|february|march|april|may|june|july|august|september|october|november|december/i.test(fullText);
                        if (!isDate) {
                            isHeading = true;
                        }
                    }
                }
                if (isHeading) {
                    addHeading(fullText);
                } else {
                    addRichParagraph(paraTokens);
                }
            });
        }
    });
};

export const generateAgreementPDF = (role, userData, signatureImg = null, heenaSignatureImg = null, template = null) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageW - (margin * 2);
    let y = margin;

    // Draw full-width header logo at the top
    try {
        const headerHeight = 44; // Increased further to perfect the aspect ratio
        doc.addImage(hedenaLogo, 'JPEG', 0, 0, pageW, headerHeight);
        y = headerHeight; // NO GAP: line will draw exactly at the bottom of the image
    } catch (e) {
        console.error('Header logo error:', e);
    }

    const addTitle = (text) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(20, 62, 120);
        const split = doc.splitTextToSize(text.toUpperCase(), contentWidth);
        split.forEach(line => {
            if (y > pageH - margin - 15) { doc.addPage(); y = margin; }
            doc.text(line, pageW / 2, y, { align: 'center' });
            y += 6;
        });
        y += 4;
    };

    const addHeading = (text) => {
        // If we are getting close to the bottom, push the heading to the next page
        // so it doesn't end up stranded without its paragraph
        if (y > pageH - margin - 30) {
            doc.addPage();
            y = margin;
        } else if (y > margin + 5) {
            y += 4; // Add top spacing before heading
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(20, 62, 120);
        const split = doc.splitTextToSize(text, contentWidth);
        split.forEach(line => {
            if (y > pageH - margin - 15) { doc.addPage(); y = margin; }
            doc.text(line, margin, y);
            y += 5;
        });
        y += 2;
    };

    const addParagraph = (text) => {
        addRichParagraph([{ text: text, style: {} }]);
    };

    const addRichParagraph = (tokens) => {
        const lineHeight = 4.5;

        const fullText = tokens.map(t => t.text).join('');
        const isDateLine = fullText.includes("is executed on this");

        const getWidth = (txt, fontType) => {
            doc.setFont('helvetica', fontType);
            const pSize = isDateLine ? 10 : 9;
            doc.setFontSize(pSize);

            return doc.getTextWidth ? doc.getTextWidth(txt) : (doc.getStringUnitWidth(txt) * doc.internal.getFontSize() / doc.internal.scaleFactor);
        };

        let lines = [];
        let currentLine = [];
        let currentLineWidth = 0;

        tokens.forEach(token => {
            let text = token.text.replace(/₹/g, 'Rs. ').replace(/\s+/g, ' ');
            if (!text.trim() && text !== ' ') return;

            let fontType = 'normal';
            if (token.style.bold && token.style.italic) fontType = 'bolditalic';
            else if (token.style.bold) fontType = 'bold';
            else if (token.style.italic) fontType = 'italic';

            const words = text.split(/(\s+)/);
            words.forEach(word => {
                if (!word) return;
                const wWidth = getWidth(word, fontType);

                // If it's a word and exceeds, push line
                if (currentLineWidth + wWidth > contentWidth && word.trim() !== '') {
                    lines.push(currentLine);
                    currentLine = [];
                    currentLineWidth = 0;
                    word = word.replace(/^\s+/, ''); // strip leading space for new line
                    if (!word) return; // if it was just a space, skip it
                }

                if (word) {
                    currentLine.push({ text: word, fontType, color: token.style.color, width: getWidth(word, fontType) });
                    currentLineWidth += getWidth(word, fontType);
                }
            });
        });
        if (currentLine.length > 0) lines.push(currentLine);

        // Now print lines with justification
        lines.forEach((line, index) => {
            const isLastLine = index === lines.length - 1;

            // Trim trailing spaces from line
            while (line.length > 0 && line[line.length - 1].text.trim() === '') {
                line.pop();
            }
            // Trim leading spaces from line
            while (line.length > 0 && line[0].text.trim() === '') {
                line.shift();
            }

            if (line.length === 0) return;

            let lineTotalWidth = line.reduce((sum, item) => sum + item.width, 0);
            let numSpaces = line.filter(item => item.text.trim() === '').length;
            let extraSpacePerSpace = 0;

            // Justify if not last line and has spaces
            if (!isLastLine && numSpaces > 0 && lineTotalWidth < contentWidth) {
                extraSpacePerSpace = (contentWidth - lineTotalWidth) / numSpaces;
            }

            let currentX = margin;
            if (y > pageH - margin - 15) { doc.addPage(); y = margin; }

            line.forEach(item => {
                doc.setFont('helvetica', item.fontType);
                const pSize = isDateLine ? 10 : 9;
                doc.setFontSize(pSize);
                if (item.color) {
                    const rgb = item.color.match(/\d+/g);
                    if (rgb && rgb.length === 3) doc.setTextColor(Number(rgb[0]), Number(rgb[1]), Number(rgb[2]));
                    else doc.setTextColor(51, 65, 85);
                } else {
                    doc.setTextColor(51, 65, 85);
                }

                if (item.text.trim() === '') {
                    currentX += item.width + extraSpacePerSpace;
                } else {
                    doc.text(item.text, currentX, y);
                    currentX += item.width;
                }
            });
            y += lineHeight;
        });
        y += 2; // Extra padding after paragraph
    };

    const addTable = (tableData) => {
        if (!tableData || tableData.length === 0) return;
        const numCols = Math.max(...tableData.map(r => r.length));
        if (numCols === 0) return;
        const colWidth = contentWidth / numCols;
        const cellPad = 2;
        const lineHeight = 5;

        let totalTableHeight = 0;
        const rowHeights = [];

        // Set exact font size used in table for accurate calculation
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);

        tableData.forEach((row, rowIdx) => {
            let maxLines = 1;
            row.forEach(cell => {
                const lines = doc.splitTextToSize(String(cell), colWidth - cellPad * 2);
                if (lines.length > maxLines) maxLines = lines.length;
            });
            const rowH = maxLines * lineHeight + cellPad * 2;
            totalTableHeight += rowH;
            rowHeights.push(rowH);
        });

        // Push entire table to next page if it fits on one page but not the current page
        const pageMaxSpace = pageH - margin * 2 - 10;
        const remainingSpace = pageH - margin - y - 10;
        if (totalTableHeight <= pageMaxSpace && totalTableHeight > remainingSpace) {
            doc.addPage();
            y = margin;
        }

        tableData.forEach((row, rowIdx) => {
            const rowH = rowHeights[rowIdx];
            if (y + rowH > pageH - margin - 10) { doc.addPage(); y = margin; }
            const isHeader = rowIdx === 0;
            row.forEach((cell, colIdx) => {
                const x = margin + colIdx * colWidth;
                if (isHeader) {
                    doc.setFillColor(235, 242, 255);
                    doc.rect(x, y, colWidth, rowH, 'F');
                }
                doc.setDrawColor(180, 200, 230);
                doc.setLineWidth(0.3);
                doc.rect(x, y, colWidth, rowH, 'S');
                doc.setFont('helvetica', isHeader ? 'bold' : 'normal');
                doc.setFontSize(8.5);
                doc.setTextColor(isHeader ? 20 : 51, isHeader ? 62 : 65, isHeader ? 120 : 85);
                const lines = doc.splitTextToSize(String(cell), colWidth - cellPad * 2);
                lines.forEach((ln, li) => {
                    doc.text(ln, x + cellPad, y + cellPad + lineHeight * li + 3);
                });
            });
            y += rowH;
        });
        y += 4;
    };

    const today = new Date();
    const partnerName = userData.fullName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || '____________________';

    doc.setDrawColor(20, 62, 120);
    doc.setLineWidth(0.5);
    doc.line(margin, y, pageW - margin, y);
    y += 8;

    let resolvedTitle = replacePlaceholders(template?.title || '', userData, role);
    let resolvedContent = replacePlaceholders(template?.content || '', userData, role);

    if (resolvedContent) {
        try {
            const parser = new DOMParser();
            const docParser = parser.parseFromString(resolvedContent, 'text/html');
            const paragraphs = docParser.querySelectorAll('p');

            paragraphs.forEach(p => {
                const plainText = p.textContent.trim();
                if (plainText.length < 2 || plainText.length > 200) return;

                const startsWithNumber = /^\d+(\.\d+)*\.?\s+/.test(plainText);
                const isAllUppercase = /^[A-Z0-9\s\.\&\-\(\)]+$/.test(plainText);
                const isShort = plainText.length < 100;

                let isHeading = false;
                if (isAllUppercase && isShort) {
                    if (plainText.length >= 15 || startsWithNumber) {
                        isHeading = true;
                    }
                } else if (startsWithNumber && isShort) {
                    const isDate = /january|february|march|april|may|june|july|august|september|october|november|december/i.test(plainText);
                    if (!isDate) {
                        isHeading = true;
                    }
                }

                if (isHeading) {
                    const h4 = docParser.createElement('h4');
                    while (p.firstChild) {
                        h4.appendChild(p.firstChild);
                    }
                    Array.from(p.attributes).forEach(attr => h4.setAttribute(attr.name, attr.value));
                    p.parentNode.replaceChild(h4, p);
                }
            });

            resolvedContent = docParser.body.innerHTML;
        } catch (e) {
            console.error('DOMParser heading extraction in PDF failed:', e);
        }
    }

    if (resolvedTitle) {
        addTitle(resolvedTitle);
    } else {
        addTitle("DOXEZ MEMORANDUM OF UNDERSTANDING (MOU)");
    }
    y += 3;

    if (resolvedContent) {
        addDynamicContentToPDF(doc, resolvedContent, addTitle, addHeading, addParagraph, addTable, addRichParagraph);
    } else {
        addParagraph("Agreement terms and conditions are loaded dynamically from the server.");
    }

    y += 8;

    if (y > pageH - margin - 50) { doc.addPage(); y = margin; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text("SIGNATORIES", margin, y);
    y += 8;

    if (role === 'patient') {
        // Patient Terms & Conditions: Only Hedena Healthcare Private Limited signature on the RIGHT SIDE
        const rightX = pageW - margin - 70;
        doc.text("For Hedena Healthcare Private Limited", rightX, y);
        y += 4;
        doc.setFont('helvetica', 'normal');
        doc.text("(Operating under brand DOXEZ)", rightX, y);
        y += 10;

        if (heenaSignatureImg) {
            try { doc.addImage(heenaSignatureImg, getImgFormat(heenaSignatureImg), rightX, y - 14, 50, 14); } catch (e) { console.error('Heena sig error:', e); }
        }
        doc.line(rightX, y, rightX + 60, y);
        y += 4;
        doc.text("Name: Dr. Heena Prasad", rightX, y);
        y += 4;
        doc.text("Designation: Managing Director", rightX, y);
        y += 4;
        doc.text(`Date: ${today.toLocaleDateString()}`, rightX, y);
    } else if (role === 'doctor' || role.includes('marketing') || role === 'marketing_agent' || (role === 'hospital' && !Array.isArray(signatureImg))) {
        doc.text("For Hedena Healthcare Private Limited", margin, y);
        if (role === 'doctor') {
            doc.text("Empanelled Medical Professional", pageW - margin - 70, y);
        } else if (role.includes('marketing') || role === 'marketing_agent') {
            doc.text("Outreach / Business Development Partner", pageW - margin - 70, y);
        } else if (role === 'hospital') {
            doc.text("For Hospital / Healthcare Facility", pageW - margin - 70, y);
        }

        y += 4;
        doc.setFont('helvetica', 'normal');
        doc.text("(Operating under brand DOXEZ)", margin, y);
        y += 10;

        if (heenaSignatureImg) {
            try { doc.addImage(heenaSignatureImg, getImgFormat(heenaSignatureImg), margin, y - 14, 50, 14); } catch (e) { console.error('Heena sig error:', e); }
        }
        if (signatureImg && !Array.isArray(signatureImg)) {
            try { doc.addImage(signatureImg, getImgFormat(signatureImg), pageW - margin - 60, y - 12, 40, 10); } catch (e) { console.error('Partner sig error:', e); }
        }
        doc.line(margin, y, margin + 50, y);
        doc.line(pageW - margin - 70, y, pageW - margin, y);
        y += 4;
        doc.text("Name: Dr. Heena Prasad", margin, y);
        doc.text(`Name: ${partnerName}`, pageW - margin - 70, y);

        y += 4;
        doc.text("Designation: Managing Director", margin, y);
        if (role === 'doctor') {
            doc.text(`Designation: ${userData.designation || 'Medical Specialist'}`, pageW - margin - 70, y);
        } else if (role.includes('marketing') || role === 'marketing_agent') {
            doc.text(`Designation: ${userData.designation || 'Partner'}`, pageW - margin - 70, y);
        } else if (role === 'hospital') {
            doc.text(`Designation: ${userData.designation || 'Authorized Representative'}`, pageW - margin - 70, y);
        }

        y += 4;
        doc.text(`Date: ${today.toLocaleDateString()}`, margin, y);
        doc.text(`Date: ${today.toLocaleDateString()}`, pageW - margin - 70, y);
    } else if (role === 'hospital' && Array.isArray(signatureImg)) {
        // Heena Signature on Left
        let leftY = y;
        doc.setFont('helvetica', 'normal');
        doc.text("(Operating under brand DOXEZ)", margin, leftY + 4);
        if (heenaSignatureImg) {
            try { doc.addImage(heenaSignatureImg, getImgFormat(heenaSignatureImg), margin, leftY + 14 - 14, 50, 14); } catch (e) { console.error('Heena sig error:', e); }
        }
        doc.line(margin, leftY + 14, margin + 50, leftY + 14);
        doc.text("Name: Dr. Heena Prasad", margin, leftY + 18);
        doc.text("Designation: Managing Director", margin, leftY + 22);
        doc.text(`Date: ${today.toLocaleDateString()}`, margin, leftY + 26);

        // Hospital Signatures on Right (Stacked vertically)
        let rightY = y;
        doc.text("For Hospital / Healthcare Facility", pageW - margin - 70, rightY);

        signatureImg.forEach((sigObj, idx) => {
            if (rightY > pageH - margin - 40) {
                doc.addPage();
                rightY = margin;
            }
            if (idx > 0) rightY += 10;

            if (sigObj.img) {
                try { doc.addImage(sigObj.img, getImgFormat(sigObj.img), pageW - margin - 60, rightY + 4, 40, 10); } catch (e) { console.error('Partner sig error:', e); }
            }
            doc.line(pageW - margin - 70, rightY + 16, pageW - margin, rightY + 16);
            doc.text(`Name: ${sigObj.name || 'Authorized Signatory'}`, pageW - margin - 70, rightY + 20);
            doc.text(`Designation: ${sigObj.designation || 'Authorized Representative'}`, pageW - margin - 70, rightY + 24);
            doc.text(`Date: ${today.toLocaleDateString()}`, pageW - margin - 70, rightY + 28);
            rightY += 28;
        });

        y = Math.max(leftY + 26, rightY);
    }

    return doc;
};
